import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, Loader2Icon } from 'lucide-react';
import { ButtonLink } from '../components/ui/ButtonLink';
import { workspaces } from '../data/workspaces';
type Status = 'idle' | 'submitting' | 'success';
type Field = 'name' | 'email' | 'organization' | 'workspace' | 'message';
const inputClass = 'mt-1.5 w-full rounded-xl border border-ink/10 bg-white/80 px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink-faint transition-[border-color,box-shadow] duration-150 focus:border-accent focus:outline-none focus:ring-4 focus:ring-accent/10';
export function Contact() {
  const [params] = useSearchParams();
  const initialWorkspace = workspaces.some(w => w.slug === params.get('workspace')) ? params.get('workspace') ?? '' : '';
  const [values, setValues] = useState<Record<Field, string>>({
    name: '',
    email: '',
    organization: '',
    workspace: initialWorkspace,
    message: ''
  });
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const update = (field: Field) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setValues(v => ({
      ...v,
      [field]: e.target.value
    }));
    if (errors[field]) setErrors(er => ({
      ...er,
      [field]: undefined
    }));
  };
  const validate = () => {
    const next: Partial<Record<Field, string>> = {};
    if (!values.name.trim()) next.name = 'Please enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) next.email = 'Please enter a valid work email.';
    if (!values.organization.trim()) next.organization = 'Please enter your organization.';
    if (!values.workspace) next.workspace = 'Please choose a workspace.';
    return next;
  };
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  };
  const errorText = (field: Field) => errors[field] ? <p id={`${field}-error`} className="mt-1.5 text-[13px] text-alert-ink" data-id="element-598" data-mp="1w62ud8ue13x7">
        {errors[field]}
      </p> : null;
  return <section className="pb-24 pt-14 md:pb-32 md:pt-20" data-id="element-599" data-mp="1r6zpppibkzpr">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 md:px-8 lg:grid-cols-[1fr_1.15fr] lg:gap-20" data-id="element-600" data-mp="13gm4ahu8pz45">
        <div data-id="element-601" data-mp="yiavmafoluni">
          <h1 className="text-balance text-4xl font-semibold tracking-display sm:text-5xl" data-id="element-602" data-mp="yiu3szfj54oc">Discuss your workspace.</h1>
          <p className="mt-5 text-lg leading-relaxed text-ink-soft" data-id="element-603" data-mp="1d88ecauuds5b">
            Tell us about your organization, your field, and the workflow you have in mind. We’ll follow up to arrange a
            conversation or a tailored demonstration.
          </p>
          <dl className="mt-10 space-y-5 border-t border-ink/[0.08] pt-8 text-[15px]" data-id="element-604" data-mp="frjicxskpk3u">
            <div data-id="element-605" data-mp="2remv5syctknq">
              <dt className="font-semibold" data-id="element-606" data-mp="2re4w6okb4sc4">What to expect</dt>
              <dd className="mt-1 text-ink-soft" data-id="element-607" data-mp="2rctqgj7axaid">A reply within two working days, from someone who knows your field.</dd>
            </div>
            <div data-id="element-608" data-mp="2hh8hg13tf9z4">
              <dt className="font-semibold" data-id="element-609" data-mp="2hhs0jl57c4eq">Prefer to explore first?</dt>
              <dd className="mt-1 text-ink-soft" data-id="element-610" data-mp="2mf0m7vpk6qkq">Each workspace has an interactive demo with sample data.</dd>
            </div>
          </dl>
          <ButtonLink to="/demos" variant="quiet" icon="arrow" className="mt-4" data-id="element-611" data-mp="3a5nuii3dzi7t">
            Browse demos
          </ButtonLink>
        </div>

        <div className="glass relative rounded-[28px] p-6 sm:p-9" data-id="element-612" data-mp="314q0msmnwr2y">
          <AnimatePresence mode="wait" initial={false} data-id="element-613" data-mp="316ix5f3k9ux8">
            {status === 'success' ? <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.24,
            ease: [0.23, 1, 0.32, 1]
          }} className="flex min-h-[440px] flex-col items-center justify-center text-center" role="status" data-id="element-614" data-mp="3548c6xslfpt4">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-ok text-white shadow-glow-ok" data-id="element-615" data-mp="256iw7ls75j6a">
                  <CheckIcon className="h-6 w-6" aria-hidden="true" data-id="element-616" data-mp="2064xiuchjlkx" />
                </span>
                <h2 className="mt-5 text-2xl font-semibold tracking-tight" data-id="element-617" data-mp="1vaxwxveiix08">Thank you, {values.name.split(' ')[0]}.</h2>
                <p className="mt-2 max-w-sm text-[15px] text-ink-soft" data-id="element-618" data-mp="2suiur7vr2dvf">
                  We’ve received your inquiry and will be in touch at {values.email}.
                </p>
                <ButtonLink to="/workspaces" variant="secondary" className="mt-7" data-id="element-619" data-mp="2iww3dlx9ktuk">
                  Keep exploring
                </ButtonLink>
              </motion.div> : <motion.form key="form" exit={{
            opacity: 0
          }} transition={{
            duration: 0.15
          }} onSubmit={onSubmit} noValidate className="space-y-5" data-id="element-620" data-mp="17l3lni9udgi3">
                <div className="grid gap-5 sm:grid-cols-2" data-id="element-621" data-mp="3f5k8ki2ii67f">
                  <div data-id="element-622" data-mp="3f752q6kwzn4r">
                    <label htmlFor="name" className="text-sm font-medium" data-id="element-623" data-mp="3j6vq00b8fl4j">Name</label>
                    <input id="name" autoComplete="name" value={values.name} onChange={update('name')} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-error' : undefined} className={inputClass} data-id="element-624" data-mp="3j4t49sh44e5m" />
                    {errorText('name')}
                  </div>
                  <div data-id="element-625" data-mp="1pvz7u4uhv2o">
                    <label htmlFor="email" className="text-sm font-medium" data-id="element-626" data-mp="1sql7q3ibny7">Work email</label>
                    <input id="email" type="email" autoComplete="email" value={values.email} onChange={update('email')} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-error' : undefined} className={inputClass} data-id="element-627" data-mp="6rle5kv6usw1" />
                    {errorText('email')}
                  </div>
                </div>
                <div data-id="element-628" data-mp="2hhjyvxb8u1p6">
                  <label htmlFor="organization" className="text-sm font-medium" data-id="element-629" data-mp="2hgqmogtimjzn">Organization</label>
                  <input id="organization" autoComplete="organization" value={values.organization} onChange={update('organization')} aria-invalid={!!errors.organization} aria-describedby={errors.organization ? 'organization-error' : undefined} className={inputClass} data-id="element-630" data-mp="3krnzikez8txi" />
                  {errorText('organization')}
                </div>
                <div data-id="element-631" data-mp="3oshq5ljt0mu3">
                  <label htmlFor="workspace" className="text-sm font-medium" data-id="element-632" data-mp="3owdvsadjsaiy">Workspace interest</label>
                  <select id="workspace" value={values.workspace} onChange={update('workspace')} aria-invalid={!!errors.workspace} aria-describedby={errors.workspace ? 'workspace-error' : undefined} className={inputClass} data-id="element-633" data-mp="7jkxd1qkd4jz">
                    <option value="" data-id="element-634" data-mp="cc4a5whffvnq">Choose a field</option>
                    {workspaces.map(w => <option key={w.slug} value={w.slug} data-id="element-635" data-mp="qbm38o1r9v2w">{w.name}</option>)}
                    <option value="multiple" data-id="element-636" data-mp="ldu9dohjvyw2">More than one field</option>
                  </select>
                  {errorText('workspace')}
                </div>
                <div data-id="element-637" data-mp="14x9pqfvflbox">
                  <label htmlFor="message" className="text-sm font-medium" data-id="element-638" data-mp="19xxd8rkgbaee">
                    Message <span className="font-normal text-ink-muted" data-id="element-639" data-mp="v0ybhkca6bru">(optional)</span>
                  </label>
                  <textarea id="message" rows={4} value={values.message} onChange={update('message')} placeholder="The workflow you’d like to improve, your timeline, team size…" className={`${inputClass} resize-none`} data-id="element-640" data-mp="zzrdafi7gec6" />
                </div>
                <button type="submit" disabled={status === 'submitting'} className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-accent px-5 text-[15px] font-medium text-white shadow-[0_8px_24px_-10px_rgba(43,91,232,0.7)] transition-[background-color,transform] duration-150 ease-out hover:bg-accent-ink active:scale-[0.98] disabled:opacity-70" data-id="element-641" data-mp="r04nkqyu8wyy">
                  {status === 'submitting' ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-642" data-mp="nq3cgv9hsmdq" /> Sending
                    </> : 'Send inquiry'}
                </button>
                <p className="text-center text-[12px] text-ink-muted" data-id="element-643" data-mp="dui6yn57t3z8">We’ll only use your details to respond to this inquiry.</p>
              </motion.form>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}